import React, { FunctionComponent, useState } from 'react';
import withStyles from 'react-jss';
import { Container, Grid, Button } from 'semantic-ui-react';
import { pathOr } from 'ramda';
import { Storage } from 'aws-amplify';
import { saveAs } from 'file-saver';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import capitalize from 'capitalize';
import classnames from 'classnames';

import { ClassProp, WithRouterProps } from 'helpers/types';
import Query from 'components/Query';
import Table from 'components/Table';
import { formatDate } from 'helpers/date';
import { ChartPlaceholder } from 'components/Placeholder';
import Breadcrumb from 'components/Breadcrumb';
import { CardNetwork } from 'scenes/CSVExport/CSVExport.helper';
import Loading from 'components/Loading';
import {
  Direction,
  SortDefaultColumn,
  TableHeader,
} from 'components/Table/Table';

import LIST_REQUEST_RESPONSE_FILES from './ListRequestResponseFiles.queries';
import styles from './ListRequestResponseFiles.styles';

type FileContent = {
  key: string;
  lastModified: Date;
  eTag: string;
  size: number;
  strageClass: string;
};

type FileMetadata = {
  nextContinuationToken: string;
  files: FileContent[];
};

type ListRequestAndResponseFiles = {
  responseFiles: FileMetadata;
  requestFiles: FileMetadata;
};

type QueryResponse = {
  data: ListRequestAndResponseFiles;
  loading: boolean;
};

type ProcessType =
  | 'RETRIEVAL'
  | 'ACTIVATION_DOSH'
  | 'DEACTIVATION_DOSH'
  | 'ACTIVATION_REWARD_NETWORKS'
  | 'DEACTIVATION_REWARD_NETWORKS';

type RequestResponseFilesParams = {
  cardNetwork: CardNetwork;
  processType: ProcessType;
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = WithRouterProps & {
  classes: Styles;
};

const onClickHandler = (setLoading: Function) => (
  value: string,
) => async () => {
  setLoading(true);
  const file = await Storage.get(value, {
    bucket: process.env.CN_BUCKET,
    download: true,
    region: process.env.AWS_REGION,
  });

  const csvFile = new Blob([file.Body], {
    type: 'text/csv',
  });

  saveAs(csvFile, `${(value || '').split('/').pop()}`);
  setLoading(false);
};

const tableHeader = (onClickHandler: Function): TableHeader[] => [
  {
    source: ['key'],
    label: 'Download',
    component: {
      Element: Button,
      props: {
        icon: 'cloud download',
        size: 'small',
        onClick: onClickHandler,
      },
    },
  },
  {
    source: ['key'],
    sortable: ['key'],
    label: 'Name',
    width: 'ten',
    transform: (key: string) => key.split('/').pop(),
  },
  {
    source: ['lastModified'],
    sortable: ['lastModified'],
    label: 'Last Modified',
    transform: (date: string) => formatDate(date, 'MMM DD, YYYY'),
  },
  {
    source: ['size'],
    sortable: ['size'],
    label: 'Size',
    transform: (size: number) => `${(size / 1000).toFixed(2)} KB`,
  },
];

const sortDefaultColumn: SortDefaultColumn = {
  name: 'Last Modified',
  sortable: ['lastModified'],
  direction: Direction.ASCENDING,
};

const loadMore = (
  setNextToken: Function,
  setPage: Function,
  token: string | null,
) => (direction: 'next' | 'previous') => {
  if (direction === 'next') {
    token && setNextToken((tokens: string[]) => [...tokens, token]);
    setPage((page: number) => page + 1);
  } else {
    setPage((page: number) => page - 1);
  }
};

const renderListRequestResponseFiles = (
  classes: Styles,
  processType: ProcessType,
  cardNetwork: CardNetwork,
  [setRequestNextToken, setResponseNextToken]: [Function, Function],
  [requestPage, responsePage]: [number, number],
  [setRequestPage, setResponsePage]: [Function, Function],
  [downloading, setDownloading]: [boolean, Function],
) => (queryResponse: QueryResponse) => {
  const { data, loading } = queryResponse;

  const requestFiles = pathOr([], ['requestFiles', 'files'], data);
  const responseFiles = pathOr([], ['responseFiles', 'files'], data);

  const requestToken = pathOr(
    null,
    ['requestFiles', 'nextContinuationToken'],
    data,
  );

  const responseToken = pathOr(
    null,
    ['responseFiles', 'nextContinuationToken'],
    data,
  );

  const formattedProcessType = capitalize.words(processType);
  const formattedCardNetwork = capitalize.words(cardNetwork);

  const requestLoadMore = loadMore(
    setRequestNextToken,
    setRequestPage,
    requestToken,
  );

  const responseLoadMore = loadMore(
    setResponseNextToken,
    setResponsePage,
    responseToken,
  );

  return (
    <>
      {downloading && <Loading loading />}

      <Breadcrumb
        disabledRoutes={['cn-onboarding']}
        className={classes.breadcrumb}
      />

      <Container className={classes.container}>
        {loading ? (
          <ChartPlaceholder className={classes.chartPlaceholder} />
        ) : (
          <Grid container>
            <Grid.Row>
              <Grid.Column width={16} className={classes.parameter}>
                {formattedCardNetwork} <span> &gt; </span>{' '}
                {formattedProcessType}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className={classes.table} columns={1}>
              <h4 className={classes.header}>Request Files</h4>

              {!requestFiles.length ? (
                <h5 className={classnames(classes.header, 'sub')}>
                  There is no data to display at this time
                </h5>
              ) : (
                <Table
                  splitEveryArg={10}
                  itemsKey="key"
                  tableHeader={tableHeader(onClickHandler(setDownloading))}
                  data={requestFiles}
                  sortDefaultColumn={sortDefaultColumn}
                  pagination={{
                    page: requestPage,
                    loadMore: requestLoadMore,
                    hasPrevious: requestPage > 0,
                    hasNext: !!requestToken,
                    pagesPerGroup: 10,
                  }}
                />
              )}
            </Grid.Row>

            <Grid.Row className={classes.table}>
              <h4 className={classes.header}>Response Files</h4>

              {!responseFiles.length ? (
                <h5 className={classnames(classes.header, 'sub')}>
                  There is no data to display at this time
                </h5>
              ) : (
                <Table
                  splitEveryArg={10}
                  itemsKey="key"
                  tableHeader={tableHeader(onClickHandler(setDownloading))}
                  data={responseFiles}
                  sortDefaultColumn={sortDefaultColumn}
                  pagination={{
                    page: responsePage,
                    loadMore: responseLoadMore,
                    hasPrevious: responsePage > 0,
                    hasNext: !!responseToken,
                    pagesPerGroup: 10,
                  }}
                />
              )}
            </Grid.Row>
          </Grid>
        )}
      </Container>
    </>
  );
};

const ListRequestResponseFiles: FunctionComponent<Props> = ({
  classes,
  location,
}) => {
  const loadingState = useState<boolean>(false);
  const [requestNextToken, setRequestNextToken] = useState<string[]>([]);
  const [responseNextToken, setResponseNextToken] = useState<string[]>([]);
  const [requestPage, setRequestPage] = useState<number>(0);
  const [responsePage, setResponsePage] = useState<number>(0);

  const requestToken = requestNextToken[requestPage - 1];
  const responseToken = responseNextToken[responsePage - 1];

  const { cardNetwork, processType } = (queryString.parse(
    location.search,
  ) as unknown) as RequestResponseFilesParams;

  const baseParams = {
    cardNetwork,
    processType,
    maxKeys: 100,
  };

  return (
    <Query
      fetchPolicy="network-only"
      enableProgressInfo
      query={LIST_REQUEST_RESPONSE_FILES}
      render={renderListRequestResponseFiles(
        classes,
        processType,
        cardNetwork,
        [setRequestNextToken, setResponseNextToken],
        [requestPage, responsePage],
        [setRequestPage, setResponsePage],
        loadingState,
      )}
      variables={{
        requestParams: {
          ...baseParams,
          operationType: 'REQUEST',
          nextToken: requestToken,
        },
        responseParams: {
          ...baseParams,
          operationType: 'RESPONSE',
          nextToken: responseToken,
        },
      }}
    />
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(ListRequestResponseFiles);
